import React, { useCallback, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Pager, Paging, Scrolling, Summary, TotalItem } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { Card, Form } from 'react-bootstrap';
import { Button as DevXButton } from 'devextreme-react/button';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from 'Common/Loader';
import type { ValueChangedEvent } from 'devextreme/ui/date_range_box';
import DateRangeBox from 'devextreme-react/cjs/date-range-box';
import { GetGoldRateLogData } from '../services/advanceReport.service';

const GoldRateLogReportDataList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const msInDay = 1000 * 60 * 60 * 24;
  const now = new Date();
  const initialValue: [Date, Date] = [
    new Date(now.getTime() - msInDay * 3),
    new Date(now.getTime() + msInDay * 3),
  ];
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(initialValue);

  const onCurrentValueChange = useCallback((e: ValueChangedEvent) => {
    const value = e.value as [Date, Date] | undefined;
    if (value && value[0] && value[1]) {
      const [startDate, endDate] = value;
      setSelectedRange([startDate, endDate]);
    } else {
      setSelectedRange(null);
    }
  }, []);

  function formatDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const [dataGridInstance, setDataGridInstance] = useState<any>(null);
  const gridRef = useRef(null);

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "uid",
      load: async () => {
        try {
          setLoading(true);

          const parameterList = {
            searchValue: searchValue ?? undefined,
            fromDate: selectedRange ? formatDateTime(selectedRange[0]) : undefined,
            toDate: selectedRange ? formatDateTime(selectedRange[1]) : undefined
          };

          const response = await GetGoldRateLogData(parameterList);
          const dataWithId = (response.data ?? []).map((item: any, index: number) => ({
            ...item,
            uid: `${Date.now()}_${index}`
          }));

          return {
            data: dataWithId,
            totalCount: dataWithId.length
          };
        } catch (error) {
          console.error("Data loading error:", error);
          return { data: [], totalCount: 0 };
        } finally {
          setLoading(false);
        }
      }
    });
  }, [searchValue, selectedRange]);

  const renderSerialNumber = useCallback((cellData: any) => {
    const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
    return <span>{rowIndex}</span>;
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!dataGridInstance) return;

    setLoading(true);
    try {
      const dataSource = dataGridInstance.getDataSource();
      dataSource.load().then((data: any[]) => {
        const columns = [
          { key: 'metal', name: 'Metal' },
          { key: 'kt', name: 'KT' },
          { key: 'purity', name: 'Purity' },
          { key: 'goldRate', name: 'Gold Rate' },
          { key: 'ogRate', name: 'Old Gold Rate' },
          { key: 'createdOn', name: 'Created On' },
        ];

        let excelData: any[] = [];
        if (data.length > 0) {
          excelData.push(columns.reduce((obj, col) => ({
            ...obj,
            [col.name]: col.name
          }), {}));

          data.forEach(item => {
            excelData.push({
              'Metal': item.metal || '',
              'KT': item.kt || '',
              'Purity': item.purity ?? 0,
              'Gold Rate': item.goldRate ?? 0,
              'Old Gold Rate': item.ogRate ?? 0,
              'Created On': item.createdOn ? item.createdOn.split("T")[0] : ''
            });
          });
        }

        const worksheet = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });
        worksheet['!cols'] = columns.map(col => ({ width: col.name.length + 5 }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "GoldRateLog");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `GoldRateLog_${new Date().toISOString().slice(0, 10)}.xlsx`);
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setLoading(false);
    }
  }, [dataGridInstance]);

  return (
    <React.Fragment>

      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className='d-flex justify-content-between align-items-center'>
              <h5 className='m-0'>Gold Rate Log Report</h5>
              {/* <DevXButton
                icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                hint="Export to Excel"
                onClick={handleExportExcel}
              /> */}
            </Card.Header>

            <div className='d-flex flex-wrap align-items-center p-2'>


              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ width: '200px', marginRight: '20px', marginTop: '20px' }}
              />

              <DateRangeBox
                width={300}
                ref={gridRef}
                defaultValue={['', '']}
                displayFormat="dd/MM/yyyy"
                showClearButton={true}
                acceptCustomValue={false}
                onValueChanged={onCurrentValueChange}
              />
            </div>

            <Card.Body>
              <DataGrid
                dataSource={dataSource}
                showRowLines={true}
                keyExpr="uid"
                showBorders={true}
                columnAutoWidth={true}
                onInitialized={(e) => setDataGridInstance(e.component)}
                onContentReady={(e) => {
                  const hasData = e.component.totalCount() > 0;
                  e.component.option("pager.visible", hasData);
                  e.component.option("paging.visible", hasData);
                }}
              >
                <Pager
                  showPageSizeSelector={true}
                  allowedPageSizes={[10, 25, 50, 100]}
                  showNavigationButtons={true}
                  displayMode="full"
                  showInfo={true}
                />
                <Summary>
                  <TotalItem />
                </Summary>
                <Scrolling mode='standard' useNative={true} />
                <Paging defaultPageSize={10} />

                <Column caption="Sr.No." cellRender={renderSerialNumber} width={100} />
                <Column dataField="metal" caption="Metal" width={100} />
                <Column dataField="kt" caption="Metal KT" width={150} />
                <Column dataField="purity" caption="Purity" width={100} dataType="number" format="#,##0.000" />
                <Column dataField="goldRate" caption="Gold Rate" dataType="number" format="#,##0.00" />
                <Column dataField="ogRate" caption="Old Gold Rate" dataType="number" format="#,##0.00" />
                <Column
                  dataField="createdOn"
                  caption="Created On"
                  cellRender={(cellData) => {
                    if (!cellData.value) return "";
                    const date = new Date(cellData.value);
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  }}
                />
              </DataGrid>
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default GoldRateLogReportDataList;
